import { Select, SelectOption, SelectOptions } from '@pay-com/components'
import { Controller, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useGetMccsQuery } from 'generated/graphql'
import { ICustomFieldComponentProps } from 'types'
import { BusinessActivityForm } from 'pages/Onboarding/OnboardingRegion/UsRegion/UsRegionStepsConfigs/BusinessActivity/business-activity-schema'
import { getErrorMessage } from 'utils/get-error-message'
import { DEFAULT_MCC_CARD_SCHEMAS } from 'consts'

type MccCodesFieldProps = ICustomFieldComponentProps<
  BusinessActivityForm,
  'mccCodes'
>

export const MccCodesField = ({
  name,
  label,
  disabled
}: MccCodesFieldProps) => {
  const { data, loading } = useGetMccsQuery()
  const { control } = useFormContext<BusinessActivityForm>()
  const { t } = useTranslation('common')
  const { t: tErrors } = useTranslation('errors')

  const options =
    data?.mccs?.mccs?.map(mcc => ({
      value: String(mcc.mcc),
      label: `${mcc.mcc} - ${mcc.description}`
    })) ?? []

  const optionsMap = Object.fromEntries(options.map(opt => [opt.value, opt]))

  const portalId = `${String(name).replaceAll('.', '-').replaceAll(':', '-')}-multi-portal`

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => {
        const entityMccs = field.value ?? []
        const activeCodes = entityMccs
          .filter(mcc => !mcc.archived)
          .map(mcc => String(mcc.code))

        const currentValue: SelectOption[] = activeCodes.map(
          code => optionsMap[code] ?? { value: code, label: code }
        )

        const handleChange = (selected: SelectOptions | null) => {
          const selectedCodes = new Set(
            selected?.map((opt: SelectOption) => opt.value) ?? []
          )

          const updated = entityMccs.map(mcc => ({
            ...mcc,
            archived: !selectedCodes.has(String(mcc.code))
          }))

          const existingCodes = new Set(entityMccs.map(mcc => String(mcc.code)))

          const newMccs = [...selectedCodes]
            .filter(code => !existingCodes.has(code))
            .map(code => ({
              id: `local-${code}`,
              code: Number(code),
              cardSchemas: DEFAULT_MCC_CARD_SCHEMAS,
              archived: false
            }))

          field.onChange([...updated, ...newMccs])
        }

        return (
          <div>
            <Select
              isMulti
              value={currentValue}
              options={options}
              onChange={handleChange}
              onBlur={field.onBlur}
              portalOptions={{
                portalSelector: `#${portalId}`,
                zIndex: 1000
              }}
              label={label}
              placeholder={t('businessActivity.fields.mccCodes.placeholder')}
              disabled={disabled || loading}
              error={Boolean(fieldState.error)}
              errorMessage={getErrorMessage(tErrors, fieldState.error?.message)}
              size="medium"
              menuPosition="fixed"
              menuShouldBlockScroll
            />
            <div id={portalId} />
          </div>
        )
      }}
    />
  )
}
